import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { generationApi } from '../services/adminApi';
import { queryKeys } from '../utils/queryKeys';
import type {
  CreateGenerationJobInput,
  PreviewPromptInput,
  GenerationJobStatus,
} from '../types/generation';

/**
 * Hook to list generation jobs with pagination
 */
export function useGenerationJobs(params?: {
  page?: number;
  pageSize?: number;
  status?: GenerationJobStatus;
}) {
  return useQuery({
    queryKey: queryKeys.generationJobs.list(params),
    queryFn: () => generationApi.listJobs(params),
    staleTime: 30 * 1000, // 30 seconds (matches polling interval)
  });
}

/**
 * Hook to fetch a single generation job with its activities
 */
export function useGenerationJob(jobId: string) {
  return useQuery({
    queryKey: queryKeys.generationJobs.detail(jobId),
    queryFn: () => generationApi.getJob(jobId),
    enabled: !!jobId,
    staleTime: 10 * 1000,
  });
}

/**
 * Hook to preview an interpolated prompt
 */
export function usePreviewPrompt() {
  return useMutation({
    mutationFn: (input: PreviewPromptInput) => generationApi.previewPrompt(input),
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to preview prompt');
    },
  });
}

/**
 * Hook to submit a new generation job
 */
export function useSubmitGenerationJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateGenerationJobInput) => generationApi.submitJob(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.generationJobs.lists(),
      });
      toast.success('Generation job submitted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to submit generation job');
    },
  });
}

/**
 * Hook to retry a failed generation job
 */
export function useRetryJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => generationApi.retryJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.generationJobs.all,
      });
      toast.success('Job retry submitted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to retry job');
    },
  });
}

/**
 * Hook to delete a generation job
 */
export function useDeleteJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => generationApi.deleteJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.generationJobs.all,
      });
      toast.success('Job deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete job');
    },
  });
}

/**
 * Hook to mark an AI-generated activity as reviewed
 */
export function useMarkActivityReviewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: number) => generationApi.markActivityReviewed(activityId),
    onSuccess: () => {
      // Invalidate both generation jobs and activities to update review status
      queryClient.invalidateQueries({
        queryKey: queryKeys.generationJobs.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.activities.all,
      });
      toast.success('Activity marked as reviewed');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to mark activity as reviewed');
    },
  });
}
